/**
 * Choosing a microphone that actually carries signal.
 *
 * `getUserMedia({ audio: true })` lets the browser choose, and it does not choose the system
 * default. On any machine with Background Music, Loopback, BlackHole, Teams or Zoom
 * installed there are virtual inputs available, and a loopback input with nothing playing
 * produces a recording that is the right length, correctly encoded, and completely empty.
 * Everything downstream looks healthy, which makes it painful to diagnose.
 *
 * Measured on a real machine with Background Music installed:
 *
 *   { audio: true }                     -> Background Music (Virtual)
 *   { deviceId: { ideal: 'default' } }  -> Background Music (Virtual)
 *   { deviceId: { exact: 'default' } }  -> Default - MacBook Pro Microphone
 *
 * `ideal` is a preference the browser is free to ignore, and it does. Only `exact` actually
 * pins the operating system's own input, which is what a person means by "my microphone".
 *
 * A note on what this deliberately does NOT do: an earlier version probed each device and
 * rejected any that returned digital silence, on the theory that a real microphone always
 * has a noise floor. That is false. macOS noise suppression emits exact zeros in a quiet
 * room, so the probe rejected a perfectly good built-in microphone and fell through to a
 * worse one. Silence cannot distinguish "wrong device" from "quiet room", so it is reported
 * to the person — who can tell the difference — rather than acted on automatically.
 */

/**
 * Devices that are usually not microphones. Used only for ordering the fallback, never to
 * hide a device: someone routing a USB mixer through a virtual device must still be able to
 * pick it.
 */
const LIKELY_VIRTUAL =
  /(background music|loopback|blackhole|soundflower|vb-?cable|virtual|aggregate|multi-output|teams|zoom|obs|krisp|voicemeeter|ishowu)/i;

export interface MicChoice {
  readonly stream: MediaStream;
  readonly label: string;
  /** How the device was arrived at, for the console when something looks wrong. */
  readonly via: 'saved' | 'system-default' | 'ranked-fallback';
}

/**
 * Order candidates for the fallback path: an explicit choice, then the system default, then
 * anything that does not look virtual, then the rest.
 */
export function rankInputs(
  devices: readonly MediaDeviceInfo[],
  preferred?: string,
): MediaDeviceInfo[] {
  const inputs = devices.filter((d) => d.kind === 'audioinput' && d.deviceId);
  return [...inputs].sort((a, b) => score(a) - score(b));

  function score(device: MediaDeviceInfo): number {
    if (preferred && device.deviceId === preferred) return 0;
    if (device.deviceId === 'default') return 1;
    return LIKELY_VIRTUAL.test(device.label) ? 3 : 2;
  }
}

async function open(deviceId: string): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } });
}

export async function openWorkingMicrophone(preferred?: string): Promise<MicChoice> {
  // An explicit choice wins outright — the person picked it, possibly because our guess was
  // wrong for their setup.
  if (preferred) {
    try {
      const stream = await open(preferred);
      return { stream, label: labelOf(stream, 'saved input'), via: 'saved' };
    } catch {
      // Saved device is gone or in use; fall through rather than refusing to record.
    }
  }

  try {
    const stream = await open('default');
    return { stream, label: labelOf(stream, 'system default'), via: 'system-default' };
  } catch {
    // Not every browser exposes a device literally called 'default' (Firefox and Safari do
    // not), so this is an ordinary path, not an error.
  }

  // Ask for anything to unlock device labels, then re-pick using them.
  const primer = await navigator.mediaDevices.getUserMedia({ audio: true });
  const candidates = rankInputs(await navigator.mediaDevices.enumerateDevices(), preferred);
  const best = candidates[0];
  const current = primer.getAudioTracks()[0]?.getSettings().deviceId;

  if (!best || best.deviceId === current) {
    return { stream: primer, label: labelOf(primer, 'microphone'), via: 'ranked-fallback' };
  }

  primer.getTracks().forEach((track) => track.stop());
  try {
    const stream = await open(best.deviceId);
    return { stream, label: labelOf(stream, best.label), via: 'ranked-fallback' };
  } catch {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    return { stream, label: labelOf(stream, 'microphone'), via: 'ranked-fallback' };
  }
}

function labelOf(stream: MediaStream, fallback: string): string {
  return stream.getAudioTracks()[0]?.label || fallback;
}
